import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { getModules, getRoleMatrix, updateRoleMatrix } from "../api/rbac-api";
import type {
	ModuleResource,
	RoleMatrixResource,
	RoleResource,
	UpdateRoleMatrixPayload,
} from "../types/RolesPermissions.type";

export const ROLE_MATRIX_KEYS = {
	all: ["rbac", "matrix"] as const,
	byRole: (roleId: number) => [...ROLE_MATRIX_KEYS.all, roleId] as const,
	modules: () => ["rbac", "modules"] as const,
};

export const useModules = (enabled = true): UseQueryResult<ModuleResource[]> => {
	return useQuery({
		queryKey: ROLE_MATRIX_KEYS.modules(),
		queryFn: getModules,
		enabled,
	});
};

export const useRoleMatrix = (roleId: number | undefined): UseQueryResult<RoleMatrixResource> => {
	return useQuery({
		queryKey: ROLE_MATRIX_KEYS.byRole(roleId as number),
		queryFn: () => getRoleMatrix(roleId as number),
		enabled: typeof roleId === "number",
	});
};

export interface AllRoleMatricesResult {
	matricesByRoleId: Map<number, RoleMatrixResource>;
	isLoading: boolean;
	isError: boolean;
}

export const useAllRoleMatrices = (roles: RoleResource[] | undefined): AllRoleMatricesResult => {
	const safeRoles = roles ?? [];

	const results = useQueries({
		queries: safeRoles.map((role) => ({
			queryKey: ROLE_MATRIX_KEYS.byRole(role.id),
			queryFn: () => getRoleMatrix(role.id),
		})),
	});

	const matricesByRoleId = new Map<number, RoleMatrixResource>();
	results.forEach((result) => {
		if (result.data) {
			matricesByRoleId.set(result.data.role_id, result.data);
		}
	});

	return {
		matricesByRoleId,
		isLoading: results.some((result) => result.isLoading),
		isError: results.some((result) => result.isError),
	};
};

export const useUpdateRoleMatrix = (
	roleId: number | undefined
): UseMutationResult<RoleMatrixResource, unknown, UpdateRoleMatrixPayload> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: UpdateRoleMatrixPayload) => updateRoleMatrix(roleId as number, payload),
		onSuccess: (data) => {
			queryClient.setQueryData(ROLE_MATRIX_KEYS.byRole(roleId as number), data);
		},
	});
};

export interface UpdateRoleMatrixForRoleVariables {
	roleId: number;
	payload: UpdateRoleMatrixPayload;
}

/**
 * Like useUpdateRoleMatrix but not bound to a single roleId at hook-call time —
 * used by the multi-role grid to issue one PUT per dirty role, each targeting
 * a different role's matrix cache entry.
 */
export const useUpdateAnyRoleMatrix = (): UseMutationResult<
	RoleMatrixResource,
	unknown,
	UpdateRoleMatrixForRoleVariables
> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ roleId, payload }: UpdateRoleMatrixForRoleVariables) => updateRoleMatrix(roleId, payload),
		onSuccess: (data, variables) => {
			queryClient.setQueryData(ROLE_MATRIX_KEYS.byRole(variables.roleId), data);
		},
	});
};
